import 'chartjs-plugin-datalabels';

import Chart, { ChartTooltipItem } from 'chart.js';
import { AnnotationOptions } from 'chartjs-plugin-annotation';

import {
  CHART_BODY_FONT_SIZES,
  CHART_COLORS_CORP,
  CHART_COLORS_LEGACY,
  CHART_FONTS_CORP,
  CHART_FONTS_LEGACY,
  DARK_BLUE,
  FONT_SCALE_FACTOR,
  MAXIMUM_FONT_SIZE,
  MINIMUM_FONT_SIZE,
  WHITE,
} from '../../constants/fluid-chart.constants';
import { FluidBreakpoint } from '../resize/responsive-utils';

/**
 * An interface for the chartJS dataset attribute so we can pass it in to charts with mutliple datasets
 */
export interface DataSet {
  data: number[];
  backgroundColor: string;
  datalabels: object; // For overriding the color in each datalabel so the stack isn't all the same color
  tooltips: object; // For overriding the tooltip for each portion of the stacked bar
  label?: string;
  borderColor?: string;
  barPercentage?: number;
  categoryPercentage?: number;
}

export interface DatasetOverrides {
  fill?: boolean; // Whether or not the line is "filled" or simple a line
  borderDash?: number[]; // The pattern (if any) of the line itself; length and spacing of dashes. @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/setLineDash
  label?: string; // The label shown on the legend
  backgroundColor?: string; // The line FILL color
  borderColor?: string; // The line color
  lineTension?: number; // Controls the bezier curve - set to 0 for straight lines.
  showDataLabels?: boolean; // Controls visibility of data labels
}

export class ChartUtils {
  /**
   * Builds a dataset for charts with multiple datasets
   * @param chartData - number[][], data for the chart
   * @param colors - string[], color array for chart
   * @param datasetOverrides
   */
  static datasetInterfaceBuilder = (
    chartData: number[][],
    colors: string[],
    datasetOverrides?: DatasetOverrides[]
  ) => {
    const datasets: DataSet[] = [];
    const tooltips: object = {};
    for (let i = 0; i < chartData.length; i++) {
      const datalabels = ChartUtils.getDataLabel(i, colors, datasetOverrides);
      if (i < datasetOverrides?.length) {
        datasets.push({
          data: chartData[i],
          backgroundColor: colors[i],
          datalabels: datalabels,
          tooltips: tooltips,
          borderColor: colors[i],
          ...datasetOverrides[i],
        });
      } else {
        datasets.push({
          data: chartData[i],
          backgroundColor: colors[i],
          datalabels: datalabels,
          tooltips: tooltips,
          borderColor: colors[i],
        });
      }
    }
    return datasets;
  };

  /**
   * Creates the label object
   * @param index
   * @param colors
   * @param datasetOverrides
   */
  private static getDataLabel(
    index: number,
    colors: string[],
    datasetOverrides?: DatasetOverrides[]
  ) {
    const dataLabels: any = {};
    if (datasetOverrides && datasetOverrides[index].showDataLabels === false) {
      dataLabels.display = false;
    }
    if (
      datasetOverrides &&
      index < datasetOverrides.length &&
      datasetOverrides[index].backgroundColor
    ) {
      const textColor = ChartUtils.pickTextColor(
        datasetOverrides[index].backgroundColor,
        WHITE,
        DARK_BLUE
      );
      dataLabels.color = textColor;
      dataLabels.borderColor = textColor;
      dataLabels.backgroundColor = datasetOverrides[index].backgroundColor;
      dataLabels.borderRadius = 10;
      dataLabels.borderWidth = 2;
    } else {
      const textColor = ChartUtils.pickTextColor(
        colors[index],
        WHITE,
        DARK_BLUE
      );
      dataLabels.color = textColor;
    }
    return dataLabels;
  }

  /**
   * Truncates and (optionally) currency formats values for readability
   * e.g. 200000 would become $200.0K, assuming a precision of 1 and a symbol of '$'
   * @param value - number, the value you wish to format
   * @param precision - number, the number of digits after the decimal place you wish to have
   * @param optionalSymbol - string, prepended to the value, used for currencies
   */
  static toTruncated = (
    value: number,
    precision: number,
    optionalSymbol: string
  ): string => {
    if (value < 1e3) return `${optionalSymbol}${value}`;
    if (value >= 1e3 && value < 1e6)
      return `${optionalSymbol}${(value / 1e3).toFixed(precision)}K`;
    if (value >= 1e6 && value < 1e9)
      return `${optionalSymbol}${(value / 1e6).toFixed(precision)}M`;
    if (value >= 1e9 && value < 1e12)
      return `${optionalSymbol}${(value / 1e9).toFixed(precision)}B`;
    if (value >= 1e12)
      return `${optionalSymbol}${(value / 1e12).toFixed(precision)}T`;
    else return 'error truncating number';
  };

  /**
   * Calculates the relative luminance of a color
   * @param color - string, hex color code string
   */
  static calculateLuminance = (color: string): number => {
    // Remove '#' if exists
    const formatted = color.charAt(0) === '#' ? color.substr(1, 7) : color;
    // Convert from hex to base 10
    const r = parseInt(formatted.substring(0, 2), 16);
    const g = parseInt(formatted.substring(2, 4), 16);
    const b = parseInt(formatted.substring(4, 6), 16);
    const rgbCalc = [r / 255, g / 255, b / 255];
    const rgb = rgbCalc.map((x) => {
      if (x <= 0.03928) return x / 12.92;
      else return Math.pow((x + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
  };

  /**
   * Calculates the contrast using relative luminance to determine
   * whether to use black or white text. Takes in three 6-digit hex
   * color codes as strings.
   * Calculates the contrast ratio between the lightText color and chart color
   * and compares it to the contrast ration between the darkText color and the
   * chart color. Returns the text color with the highest contrast ratio
   * @param chartColor - string, color code
   * @param lightText - string, color code for the desired light text
   * @param darkText - string, color code for the desired dark text
   */
  static pickTextColor = (
    chartColor: string,
    lightText: string,
    darkText: string
  ): string => {
    const chartL = ChartUtils.calculateLuminance(chartColor);
    const lightL = ChartUtils.calculateLuminance(lightText);
    const darkL = ChartUtils.calculateLuminance(darkText);
    return (chartL + 0.05) / (darkL + 0.05) > (lightL + 0.05) / (chartL + 0.05)
      ? darkText
      : lightText;
  };

  /**
   * Gets the correct font size for Chart "ticks" & "legend" based on
   * the active viewport size / breakpoint.
   * @param chartWidth - the width of the chart
   */
  static getChartBodyFontSize = (chartWidth: number): number => {
    switch (true) {
      case chartWidth >= FluidBreakpoint.LARGE:
        return CHART_BODY_FONT_SIZES.LARGE;
      case chartWidth >= FluidBreakpoint.MEDIUM:
        return CHART_BODY_FONT_SIZES.MEDIUM;
      case chartWidth >= FluidBreakpoint.SMALL:
        return CHART_BODY_FONT_SIZES.SMALL;
      default:
        return CHART_BODY_FONT_SIZES.X_SMALL;
    }
  };

  /**
   * Scales axis font size according to the chart width, then updates chart
   * @param chart - Chart instance
   */
  static updateChart = (chart: Chart) => {
    if (chart === undefined) return;
    const scaledFont = ChartUtils.getChartBodyFontSize(chart.width);

    !!chart.options.scales &&
      (chart.options.scales.xAxes[0].ticks.fontSize = scaledFont);
    !!chart.options.scales &&
      (chart.options.scales.yAxes[0].ticks.fontSize = scaledFont);
    !!chart.options.tooltips &&
      (chart.options.tooltips.titleFontSize = scaledFont + 2);
    !!chart.options.tooltips &&
      (chart.options.tooltips.bodyFontSize = scaledFont);
    !!chart.options.legend.display &&
      (chart.options.legend.labels.fontSize = scaledFont);

    chart.update();
    chart.render();
  };

  /**
   * Updates the chart dataset when the chartData is updated
   * Takes in either a data array or a data double array
   * @param chart - Chart, chart context
   * @param data - number[], the data if there is only 1 dataset
   * @param dataArr - number[][], the data if it's multiple datasets
   * @param colors - string[], color array for chart
   * @param datasetOverrides
   */
  static dataHandler = (
    chart: Chart,
    data?: number[],
    dataArr?: number[][],
    colors?: string[],
    datasetOverrides?: DatasetOverrides[]
  ) => {
    if (chart && (data || dataArr)) {
      if (data && !dataArr) chart.data.datasets[0].data = data;
      if (dataArr && colors)
        chart.data.datasets = ChartUtils.datasetInterfaceBuilder(
          dataArr,
          colors,
          datasetOverrides
        );
      chart.update();
      chart.render();
    }
  };

  /**
   * Updates the chart labels when the chartLabels are updated
   * Takes in either a label array or a label double array
   * @param chart - Chart, chart context
   * @param label - string[], the label if there is only 1 dataset
   * @param labelArr - string[][], the label if it's multiple datasets
   */
  static labelHandler = (
    chart: Chart,
    label?: string[],
    labelArr?: string[][]
  ) => {
    if (
      chart !== undefined &&
      (label !== undefined || labelArr !== undefined)
    ) {
      chart.data.labels = label !== undefined ? label : labelArr;
      chart.update();
      chart.render();
    }
  };

  /**
   * Updates the chart annotations when the annotations are updated
   * Takes in an array of Annotation Options
   * @param chart - Chart, chart context
   * @param annotations - AnnotationOptions[], an array of annotations
   */
  static annotationHandler = (
    chart: Chart,
    annotations: AnnotationOptions[]
  ) => {
    if (chart !== undefined && annotations !== undefined) {
      chart.options.annotation.annotations = annotations;
      chart.update();
      chart.render();
    }
  };

  /**
   * Afterbody methods will update the tooltip items
   * Only for charts which don't use multiple datasets (ie. not stackedBar)
   * @param tooltipItems - ChartTooltipItem[], ballback tooltips
   * @param tooltipData - object[], the data to be inserted in the tooltip
   */
  static tooltipAfterBody = (
    tooltipItems: ChartTooltipItem[],
    tooltipData: object[]
  ): string => {
    if (tooltipData === undefined) return '';
    else {
      const index = tooltipData[tooltipItems[0].index];
      return Object.entries(index)
        .map(([key, val]) => `${key}: ${val}`)
        .join('\n');
    }
  };

  /**
   * Scales font size based on width of chart
   * @param width - number, width of chart
   */
  static fontLabelScaler = (width: number) => {
    let size = Math.round(width / FONT_SCALE_FACTOR);
    size < MINIMUM_FONT_SIZE && (size = MINIMUM_FONT_SIZE);
    size > MAXIMUM_FONT_SIZE && (size = MAXIMUM_FONT_SIZE);
    return size;
  };

  /**
   * Convert a string if it's under 9 characters to a truncated version to fit on chart labels
   * Will treat each word in a string as their own label, so if you have 'Pacific Northwest', it will truncate to 'Pacific Northwe- st'
   * @param label - string, the string to be converted
   */
  static truncateLabel = (label: string) => {
    if (label.length < 9 || label.includes(' ')) {
      if (label.includes(' ')) {
        const labelArr = label.split(' ');
        label = '';
        labelArr.forEach((l) => (label += ChartUtils.truncateLabel(l) + ' '));
      }
      return label;
    } else {
      const truncated = label.substring(0, 7);
      return truncated + '- ' + label.substring(7, label.length);
    }
  };

  static createColorsSequence = (colorsBase: string[], data: []) => {
    const colorSequence = [];
    let colorCopy = [...colorsBase];
    for (let i = 0; i < data.length; i++) {
      if (!colorCopy[i]) {
        colorCopy = [...colorCopy, ...colorsBase];
        colorSequence.push(colorCopy[i]);
      } else {
        colorSequence.push(colorCopy[i]);
      }
    }

    return colorSequence;
  };
}

export const FluidChartStyles = {
  default: {
    colors: CHART_COLORS_CORP,
    fonts: CHART_FONTS_CORP,
  },
  legacy: {
    colors: CHART_COLORS_LEGACY,
    fonts: CHART_FONTS_LEGACY,
  },
  lm: {
    colors: CHART_COLORS_CORP,
    fonts: CHART_FONTS_CORP,
  },
  corp: {
    colors: CHART_COLORS_CORP,
    fonts: CHART_FONTS_CORP,
  },
  portal: {
    colors: CHART_COLORS_CORP,
    fonts: CHART_FONTS_CORP,
  },
};
